import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isPermissionValidForUserType } from '../utils/permissions';

export interface User {
  id: string;
  type: 'EXTERNAL' | 'INTERNAL';
  email: string;
  firstname: string;
  lastname: string;
  permissions: string[];
  roleType?: string;
  societyId?: string;
  civility?: string;
  phone?: string;
  position?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  showTutorials?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  lastLoginAt?: Date;
}

export interface UserProfile {
  id: string;
  uiTheme: string;
  uiLanguage: string;
  dashboardLayout?: Record<string, any>;
  notificationPreferences?: Record<string, any>;
  onboardingCompleted: boolean;
  onboardingStep: number;
  tutorialSeen?: Record<string, boolean>;
  lastLoginAt?: Date;
  loginCount: number;
  monthlySalesTarget?: number;
}

export interface SocietyContact {
  id: string;
  firstname: string;
  lastname: string;
  email: string | null;
  phone: string | null;
  avatar: string | null;
}

export interface Society {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  isActive: boolean;
  logo?: string;
  parentId?: string | null;
  guarantorId?: string | null;
  commercial?: SocietyContact | null;
  savManager?: SocietyContact | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  currentUser: User | null;
  profile: UserProfile | null;
  society: Society | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isImpersonating: boolean;
  originalUser: User | null;
  requirePasswordChange: boolean;
  isAuthReady: boolean;

  setAuth: (data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    profile?: UserProfile;
    society?: Society;
    isImpersonating?: boolean;
    originalUser?: User;
    requirePasswordChange?: boolean;
  }) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  updateCurrentUser: (updates: Partial<User>) => void;
  setProfile: (profile: UserProfile) => void;
  setSociety: (society: Society | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setAuthReady: (isAuthReady: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  updateProfilePreferences: (prefs: Partial<UserProfile>) => void;
  startImpersonation: (data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    profile?: UserProfile;
    society?: Society;
    originalUser: User;
  }) => void;
  stopImpersonation: (data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    profile?: UserProfile;
    society?: Society;
  }) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      currentUser: null,
      profile: null,
      society: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isImpersonating: false,
      originalUser: null,
      requirePasswordChange: false,
      isAuthReady: false,

      setAuth: (data) =>
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          currentUser: data.user,
          profile: data.profile || null,
          society: data.society || null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          isImpersonating: data.isImpersonating || false,
          originalUser: data.originalUser || null,
          requirePasswordChange: data.requirePasswordChange || false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, isAuthenticated: true }),

      setUser: (user) =>
        set({ currentUser: user, isAuthenticated: true }),

      updateCurrentUser: (updates) =>
        set((state) => ({
          currentUser: state.currentUser
            ? { ...state.currentUser, ...updates }
            : null,
        })),

      setProfile: (profile) =>
        set({ profile }),

      setSociety: (society) =>
        set({ society }),

      setLoading: (isLoading) =>
        set({ isLoading }),

      setError: (error) =>
        set({ error, isLoading: false }),

      setAuthReady: (isAuthReady) =>
        set({ isAuthReady }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          currentUser: null,
          profile: null,
          society: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          isImpersonating: false,
          originalUser: null,
          requirePasswordChange: false,
        }),

      hasPermission: (permission) => {
        const { currentUser } = get();
        if (!currentUser?.permissions) return false;

        if (currentUser.type === 'EXTERNAL' && !isPermissionValidForUserType(permission, 'EXTERNAL')) {
          return false;
        }

        if (currentUser.permissions.includes('*')) return true;
        if (currentUser.permissions.includes(permission)) return true;

        const [category] = permission.split('.');
        if (currentUser.permissions.includes(`${category}.*`)) return true;

        return false;
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some(p => hasPermission(p));
      },

      updateProfilePreferences: (prefs) =>
        set((state) => ({
          profile: state.profile
            ? { ...state.profile, ...prefs }
            : null,
        })),

      startImpersonation: (data) =>
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          currentUser: data.user,
          profile: data.profile || null,
          society: data.society || null,
          isImpersonating: true,
          originalUser: data.originalUser,
          isAuthenticated: true,
        }),

      stopImpersonation: (data) =>
        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          currentUser: data.user,
          profile: data.profile || null,
          society: data.society || null,
          isImpersonating: false,
          originalUser: null,
          isAuthenticated: true,
        }),
    }),
    {
      name: 'outil-pro-auth',
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        currentUser: state.currentUser,
        profile: state.profile,
        society: state.society,
        isImpersonating: state.isImpersonating,
        originalUser: state.originalUser,
      }),
    }
  )
);
